import axios from "axios";
import { defaultSettings } from "contexts/appsettings/defaultSettings";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { REACT_APP_BACKENDURL } from "../../../config";
import { getSubscriptionStatusLoaderData } from "../comments/getSubscriptionStatusLoaderData";
import { eAccessRights } from "../enums";
import { HierarchyReadDto } from "./HierarchyReadDto";

/**
 * Loader data returned by the hierarchy loader, including subloads.
 */
export interface HierarchyLoaderData {
  hierarchy: HierarchyReadDto;
  parentHierarchies: HierarchyReadDto[];
  accessRights: eAccessRights[];
  isHierarchySubscribed: boolean;
}

/**
 * Data returned by the hierarchy loader API endpoint.
 */
export interface HierarchyLoaderReturn {
  hierarchy: HierarchyReadDto;
  parentHierarchies: HierarchyReadDto[];
  accessRights: eAccessRights[];
}

/**
 * Construct the hierarchy loader API URL from route params.
 *
 * New routes pass the full ancestor path as query parameters
 * (`taPrefix`, `projectCode`, `studyTag`, `reportName`, `tocMajorVersion`),
 * so the backend can uniquely identify the hierarchy when tags are only
 * unique within their parent scope.
 *
 * Legacy params (`primaryHierarchyId`, `hierarchyTag`,
 * `studyName` + `reportName`) are still handled for backward compatibility
 * with legacy template and shell routes.
 *
 * @param params - Loader function params containing hierarchy identifiers
 * @returns The constructed URL for the hierarchy loader API endpoint
 */
export const getHierarchyLoaderUrl = (params: LoaderFunctionArgs["params"]): string => {
  const backEndUrl = REACT_APP_BACKENDURL.includes("_REACT_APP_BACKENDURL_")
    ? defaultSettings.backEndUrl
    : REACT_APP_BACKENDURL;

  const queryParams = new URLSearchParams();
  if (params.primaryHierarchyId) queryParams.set("primaryHierarchyId", params.primaryHierarchyId);
  if (params.hierarchyTag) queryParams.set("hierarchyTag", params.hierarchyTag);
  if (params.taPrefix) queryParams.set("taPrefix", params.taPrefix);
  if (params.projectCode) queryParams.set("projectCode", params.projectCode);
  if (params.studyTag) queryParams.set("studyTag", params.studyTag);
  if (params.reportName) queryParams.set("reportName", params.reportName);
  if (params.tocMajorVersion !== undefined)
    queryParams.set("tocMajorVersion", params.tocMajorVersion);
  if (queryParams.size === 0) {
    queryParams.set("hierarchyTag", "Corporate");
  }

  const queryString = queryParams.toString();
  return `${backEndUrl}Hierarchy/Loader?${queryString}`;
};

/**
 * Fetch hierarchy loader data (non-loader version).
 *
 * @param params - Loader function params containing hierarchy identifiers
 * @returns The hierarchy loader data
 */
export const getHierarchyLoaderData = async (
  params: LoaderFunctionArgs["params"],
): Promise<HierarchyLoaderData> => {
  const hierarchyLoader = await axios.get(getHierarchyLoaderUrl(params));
  if (!hierarchyLoader || !hierarchyLoader.data) {
    throw new Error("Invalid response from HierarchyLoader, no data returned");
  }

  const subscriptionStatusLoader = await getSubscriptionStatusLoaderData({
    params: {
      entityType: "Hierarchy",
      entityId: hierarchyLoader.data.hierarchy.id as string,
    },
  });

  const ret = {
    hierarchy: hierarchyLoader.data.hierarchy,
    parentHierarchies: hierarchyLoader.data.parentHierarchies,
    accessRights: hierarchyLoader.data.accessRights,
    isHierarchySubscribed: subscriptionStatusLoader.isSubscribed,
  };
  return ret;
};

/**
 * Loader function that returns hierarchy loader data used by routes.
 *
 * @param props - Loader args provided by react-router
 * @param props.params - Route params used to identify the hierarchy
 */
export const hierarchyLoader: LoaderFunction = async ({ params }: LoaderFunctionArgs) =>
  getHierarchyLoaderData(params);
